import { useState, useEffect } from 'react';

const WP_API_BASE = process.env.REACT_APP_WP_API_URL || 'http://cms.therapylist.com/wp-json/wp/v2';

// Custom hook for fetching WordPress content
export function useWordPressContent(endpoint, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchContent() {
      try {
        setLoading(true);
        const response = await fetch(`${WP_API_BASE}${endpoint}`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchContent();
    return () => controller.abort();
  }, [endpoint]);

  return { data, loading, error };
}

// Hook for fetching page content by slug
export function useWordPressPage(slug) {
  return useWordPressContent(`/pages?slug=${slug}`);
}

// Hook for fetching blog posts with embedded data
export function useWordPressPosts(category = '', limit = 10) {
  const endpoint = category 
    ? `/posts?categories=${category}&per_page=${limit}&_embed=author,wp:featuredmedia,wp:term`
    : `/posts?per_page=${limit}&_embed=author,wp:featuredmedia,wp:term`;
  
  return useWordPressContent(endpoint);
}

// Hook for fetching a single post by slug with embedded data
export function useWordPressPost(slug) {
  return useWordPressContent(`/posts?slug=${slug}&_embed=author,wp:featuredmedia,wp:term&context=view`);
}

// Hook for fetching custom post types (like city content)
export function useWordPressCustomPost(postType, slug) {
  return useWordPressContent(`/${postType}?slug=${slug}`);
}

// Hook for fetching homepage hero content
export function useHomepageContent() {
  const { data, loading, error } = useWordPressPage('homepage-content');
  
  return {
    heroTitle: data?.[0]?.acf?.hero_title || 'Find your perfect match in mental health care',
    heroSubtitle: data?.[0]?.acf?.hero_subtitle || 'Connect with licensed therapists who truly understand your needs.',
    heroCtaText: data?.[0]?.acf?.hero_cta_text || 'Find Therapists',
    featuresSection: data?.[0]?.acf?.features || [],
    loading,
    error
  };
}

// Utility function to decode WordPress content
export function decodeWPContent(content) {
  if (!content) return '';

  // Decode common HTML entities without using innerHTML
  const entities = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#039;': "'", '&#8217;': '\u2019', '&#8216;': '\u2018', '&#8220;': '\u201C', '&#8221;': '\u201D', '&#8211;': '\u2013', '&#8212;': '\u2014' };
  return content.replace(/&(?:amp|lt|gt|quot|#039|#821[7612]|#820[012])/g, match => entities[match] || match);
}

// Hook for fetching WordPress SEO data
export function useWordPressSEO(pageSlug = 'homepage') {
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchSEO() {
      try {
        const response = await fetch(`${WP_API_BASE}/pages?slug=${pageSlug}`, { signal: controller.signal });
        const data = await response.json();
        if (data && data[0]) {
          const seo = extractSEOFromWP(data[0]);
          setSeoData(seo);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching WordPress SEO:', error);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchSEO();
    return () => controller.abort();
  }, [pageSlug]);

  return { seoData, loading };
}

// SEO helper to extract meta from WordPress
export function extractSEOFromWP(wpPost) {
  if (!wpPost) return {};
  
  return {
    title: wpPost.yoast_head_json?.title || wpPost.title?.rendered,
    description: wpPost.yoast_head_json?.description || wpPost.excerpt?.rendered?.replace(/<[^>]*>/g, ''),
    canonical: wpPost.yoast_head_json?.canonical,
    ogTitle: wpPost.yoast_head_json?.og_title,
    ogDescription: wpPost.yoast_head_json?.og_description,
    ogImage: wpPost.yoast_head_json?.og_image?.[0]?.url
  };
} 